import { PricingContextProvider } from '@vp/react-pricing-context';
import React, { useEffect, useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter, StaticRouter } from 'react-router-dom';
import { ScreenClassProvider } from '@vp/swan';
import { useLogger, useSwanStyleKeys, useUserContext } from '@vp/ubik-context';
import i18n, { Resource } from 'i18next';
import {
    AUTH_INTERNAL_CLIENT_PARAM,
    AuthDevMode,
    AuthOptions,
} from '@99designs/design-services-common';
import { IdentityProvider } from '@99designs/design-services-common';
import {
    FeatureFlagProvider,
    VistaExperiment,
    VistaExperimentsProvider,
    featureFlagsFromUbikUserContext,
    vistaExperimentsFromUbikUserContext,
} from '@99designs/experiments';
import {
    BriefConfigProvider,
    BriefProvider,
    BriefsEnvironment,
    ProductOptionsProvider,
    StatsigConfiguration,
} from '@99designs/feature-design-services-brief';
import { AuthenticatedApolloProvider } from '@99designs/graph-utils';
import { SupportedLocale, getCountryCode } from '@99designs/i18n';
import { PAGE_TRACKING_CONFIG, PageProvider } from '@99designs/tracking';

type RouterProviderProps = {
    location?: string;
    children: React.ReactNode;
};
export const RouterProvider = ({ location, children }: RouterProviderProps) => {
    if (typeof window === 'undefined') {
        return <StaticRouter location={location ?? '/'}>{children}</StaticRouter>;
    }
    return <BrowserRouter>{children}</BrowserRouter>;
};
export interface ClientOverride {
    developmentMode: AuthDevMode;
    options?: AuthOptions;
}
export const INTERNAL_ACCESS_CLIENT: ClientOverride = {
    options: {
        connection: 'vistaprintcom-waad',
    },
    developmentMode: {
        clientID: 'dFzz8qvA9epUe28InBJYrBE4U8RuuVzQ', // This is not a secret, you can find it in the access client URL above.
        // Only set redirectUriOrigin on vista based domains. /auth-callback/ doesn't register when redirecting on localhost
        ...(process.env.NODE_ENV !== 'development' && {
            redirectUriOrigin:
                typeof window !== 'undefined'
                    ? `${window.location.origin}/auth-callback/`
                    : undefined,
        }),
    },
};
export const authDevMode = (): AuthDevMode => {
    const isAuthDevMode = process.env.NODE_ENV === 'development';
    if (!isAuthDevMode) {
        return false;
    }
    const redirectUriOrigin = typeof window !== 'undefined' ? window.location.origin : undefined;
    const clientID = process.env.AUTH0_CLIENT;
    if (!clientID) {
        return { redirectUriOrigin };
    }
    return { clientID, domain: 'oauth.cimpress.io', redirectUriOrigin };
};
export function buildAuthConfiguration(): ClientOverride {
    if (typeof window !== 'undefined') {
        const shouldUseInternalAuthClient = new URLSearchParams(window.location.search).get(
            AUTH_INTERNAL_CLIENT_PARAM
        );
        if (shouldUseInternalAuthClient) {
            return INTERNAL_ACCESS_CLIENT;
        }
    }
    return {
        developmentMode: authDevMode(),
    };
}
export interface Props {
    resources: Resource;
    locale: string;
    environment?: BriefsEnvironment;
    children: React.ReactNode;
}
const enableDebug = !!process.env['ENABLE_TRANSLATION_DEBUG'] || false;
function isProd(env?: BriefsEnvironment) {
    if (env === 'DEV' || env === 'STAGING') {
        return false;
    }
    return true;
}
export const BriefUbikProviders = ({ resources, locale, environment, children }: Props) => {
    const logger = useLogger();
    const userContext = useUserContext();

    i18n.init({
        resources,
        debug: enableDebug,
        lowerCaseLng: true, //locales are set to lower case e.g. "en-us" not "en-US"
        interpolation: {
            escapeValue: false, // React already provides escaping
        },
        nsSeparator: '*****', // namespace selector is a string delimited which defaults to ":". Overriding to *** so that urls don't get split
        returnObjects: true,
    });
    i18n.changeLanguage(userContext.locale);
    useSwanStyleKeys([
        'accordion',
        'alertBox',
        'avatar',
        'banner',
        'button',
        'carousel',
        'core',
        'datePicker',
        'grid',
        'hidden',
        'icon',
        'listbox',
        'modalDialog',
        'tabs',
        'thumbnailsHero',
        'pagination',
        'popover',
        'progressBar',
        'progressiveImage',
        'selectionSet',
        'spinner',
        'standardTile',
        'toggleSwitch',
        'utility',
        'visible',
        'zoomControls',
        'fileDropzone',
        'fileTile',
        'emptyState',
    ]);
    const experiments: VistaExperiment[] = userContext?.experiments
        ? vistaExperimentsFromUbikUserContext(userContext?.experiments, logger)
        : [];
    const featureFlags = featureFlagsFromUbikUserContext(userContext?.featureFlags);
    const [authConfig, setAuthConfig] = useState<ClientOverride>({
        developmentMode: authDevMode(),
    });
    useEffect(() => {
        setAuthConfig(buildAuthConfiguration());
    }, []);
    i18n.changeLanguage(locale);
    const currentCulture = locale as SupportedLocale;

    // Note: We used to have a `BriefExperimentsProvider` that may help with experiments, but it was
    //       removed since it was not being used; see: `b170577e6890fdf3ff1ee632c03d7e4b633edcad`.

    return (
        <BriefConfigProvider env={environment ?? 'PROD'}>
            <VistaExperimentsProvider value={experiments}>
                <FeatureFlagProvider value={featureFlags}>
                    <HelmetProvider>
                        <ScreenClassProvider>
                            <IdentityProvider locale={locale} {...authConfig}>
                                <RouterProvider>
                                    <AuthenticatedApolloProvider
                                        locale={currentCulture}
                                        environment={
                                            isProd(environment) ? 'production' : 'non-production'
                                        }
                                        endpoint={
                                            environment === 'DEV'
                                                ? 'http://graph.dockervm:49944/graph'
                                                : 'https://graph.es.vpsvc.com/graph'
                                        }
                                    >
                                        <PricingContextProvider
                                            initializationConfig={{
                                                merchantId: 'vistaprint',
                                                market: getCountryCode(currentCulture),
                                                isDevelopmentMode: !isProd(environment),
                                            }}
                                        >
                                            <StatsigConfiguration>
                                                <BriefProvider>
                                                    <ProductOptionsProvider>
                                                        <PageProvider
                                                            locale={currentCulture}
                                                            pageData={{
                                                                pageName:
                                                                    'Design Services:Expert Services Multi-Step Brief',
                                                                ...PAGE_TRACKING_CONFIG['design'],
                                                            }}
                                                        >
                                                            {children}
                                                        </PageProvider>
                                                    </ProductOptionsProvider>
                                                </BriefProvider>
                                            </StatsigConfiguration>
                                        </PricingContextProvider>
                                    </AuthenticatedApolloProvider>
                                </RouterProvider>
                            </IdentityProvider>
                        </ScreenClassProvider>
                    </HelmetProvider>
                </FeatureFlagProvider>
            </VistaExperimentsProvider>
        </BriefConfigProvider>
    );
};
